/* Vesper P1.07.2 + P1.07.9 – "Complete your ritual" in the cart drawer.
   1) Her pairing first (Search & Discovery complementary products), skipping items already in the cart.
   2) If the cart is under $95 (USD carts only) and the pairing alone wouldn't clear it,
      also suggest the cheapest in-stock product that gets the order to free shipping.
   Hides itself when there's nothing to suggest. */

const VR_FREE_SHIP_CENTS = 9500;
// Products never suggested by the $95 nudge (matched against the title, lowercase).
// 'awaken' = early-access launch behind SHOPLOCK: remove it from this list on Oct 17.
const VR_EXCLUDE_TITLES = ['awaken', 'gift card', 'sample'];

class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.sectionId = 'cart-drawer-recommendations';
    this.limit = parseInt(this.dataset.limit) || 2;
  }

  connectedCallback() {
    this.init();
    this.observeCartDrawer();
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || this._observing) return;
    this._observing = true;
    new MutationObserver(() => {
      if (cartDrawer.classList.contains('active')) setTimeout(() => this.init(), 100);
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  init() {
    const ids = [...document.querySelectorAll('cart-drawer [data-cart-item][data-product-id]')]
      .map((el) => String(el.dataset.productId));
    if (!ids.length) return this.hideSelf();
    this.build([...new Set(ids)]);
  }

  hideSelf() {
    const wrap = this.closest('.cart-drawer__recommendations-wrapper');
    if (wrap) wrap.style.display = 'none';
  }

  showSelf() {
    const wrap = this.closest('.cart-drawer__recommendations-wrapper');
    if (wrap) wrap.style.display = '';
  }

  async build(productIds) {
    const inCart = new Set(productIds);
    try {
      const [pairing, cart, catalog] = await Promise.all([
        this.getPairing(inCart),
        fetch('/cart.js').then((r) => r.json()),
        this.getCatalog().catch(() => []),
      ]);

      const pairingIds = new Set(
        pairing ? [...pairing.querySelectorAll('[data-product-id]')].map((el) => String(el.dataset.productId)) : []
      );
      const pick = this.pickThreshold(cart, catalog, inCart, pairingIds);

      if (!pairing && !pick) return this.hideSelf();

      let block = pairing;
      if (!block) {
        block = document.createElement('div');
        block.className = 'cart-drawer__recommendations vr-ritual';
        block.setAttribute('data-cart-recommendations-rendered', '');
        block.innerHTML = '<p class="vr-ritual__heading">Complete your ritual</p>';
      }
      if (pick) block.insertAdjacentHTML('beforeend', this.renderPick(pick));

      this.innerHTML = block.outerHTML;
      this.attachEventListeners();
      this.showSelf();
    } catch (error) {
      console.error('CartDrawerRecommendations:', error);
      this.hideSelf();
    }
  }

  async getPairing(inCart) {
    for (const id of inCart) {
      const url = `/recommendations/products?section_id=${this.sectionId}&product_id=${id}&limit=${this.limit}&intent=complementary`;
      const text = await (await fetch(url)).text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const rendered = doc.querySelector('[data-cart-recommendations-rendered]');
      if (!rendered) continue;
      rendered.querySelectorAll('.cart-drawer__recommendations-item').forEach((item) => {
        if (inCart.has(String(item.dataset.productId))) item.remove();
      });
      if (rendered.querySelector('.cart-drawer__recommendations-item')) return rendered;
    }
    return null;
  }

  async getCatalog() {
    if (CartDrawerRecommendations._catalog) return CartDrawerRecommendations._catalog;
    const data = await (await fetch('/products.json?limit=250')).json();
    CartDrawerRecommendations._catalog = data.products || [];
    return CartDrawerRecommendations._catalog;
  }

  pickThreshold(cart, catalog, inCart, pairingIds) {
    if (!cart || cart.currency !== 'USD' || !catalog.length) return null;
    const gap = VR_FREE_SHIP_CENTS - cart.total_price;
    if (gap <= 0) return null;

    const cents = (p) => Math.round(parseFloat(p.variants[0].price) * 100);
    const byId = new Map(catalog.map((p) => [String(p.id), p]));

    // If a pairing product already clears $95, the pairing does the job on its own.
    for (const id of pairingIds) {
      const p = byId.get(id);
      if (p && p.variants && p.variants[0] && cents(p) >= gap) return null;
    }

    const options = catalog.filter((p) => {
      const t = (p.title || '').toLowerCase();
      return (
        p.variants && p.variants.length === 1 && p.variants[0].available &&
        !inCart.has(String(p.id)) && !pairingIds.has(String(p.id)) &&
        !VR_EXCLUDE_TITLES.some((x) => t.includes(x)) &&
        cents(p) > 0 && cents(p) >= gap
      );
    });
    options.sort((a, b) => cents(a) - cents(b));
    return options[0] || null;
  }

  renderPick(p) {
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const v = p.variants[0];
    const src = p.images && p.images[0] ? p.images[0].src : '';
    const img = src ? `${src}${src.includes('?') ? '&' : '?'}width=160` : '';
    const url = `/products/${p.handle}`;
    const price = `$${parseFloat(v.price).toFixed(2)}`;
    return `
      <div class="cart-drawer__recommendations-item vr-ritual__item vr-ritual__item--threshold" data-product-id="${p.id}">
        <a href="${url}" class="vr-ritual__img" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" width="64" height="64" loading="lazy">` : ''}</a>
        <div class="vr-ritual__info">
          <a href="${url}" class="vr-ritual__title">${esc(p.title)}</a>
          <span class="vr-ritual__price">${price}</span>
          <span class="vr-ritual__perk">Unlocks free US shipping</span>
        </div>
        <button type="button" class="vr-ritual__btn cart-rec-link--add-to-cart" data-variant-id="${v.id}" aria-label="Add ${esc(p.title)} to cart">Add</button>
      </div>`;
  }

  attachEventListeners() {
    this.querySelectorAll('.cart-rec-link--add-to-cart').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e, btn));
    });
  }

  async handleAddToCart(e, btn) {
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalText = btn.textContent;
    btn.textContent = 'Adding…';
    btn.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Failed to add to cart');
      await this.refreshCart();
    } catch (error) {
      console.error('Error adding to cart:', error);
      btn.textContent = 'Try again';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    }
  }

  async refreshCart() {
    const sections = await (await fetch('/?sections=cart-drawer,cart-icon-bubble')).json();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents({ sections });
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}